import { useEffect, useState } from "react";
import { DoorOpen, Loader2, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { GlassCard, PageHeader, EmptyState, StatusBadge } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function ExitManagement() {
    const { user } = useAuth();
    const [mine, setMine] = useState({});
    const [all, setAll] = useState([]);
    const [loading, setLoading] = useState(true);
    const canManage = ["super_admin", "hr"].includes(user?.role);

    const load = async () => {
        try {
            const reqs = [api.get("/exit/me")];
            if (canManage) reqs.push(api.get("/exit/all"));
            const [m, a] = await Promise.all(reqs);
            setMine(m.data || {});
            if (a) setAll(a.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    if (loading) return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

    return (
        <div>
            <PageHeader title="Exit Management" subtitle="Resignation, asset return and exit interviews." testId="exit-header" />

            <Tabs defaultValue="mine">
                <TabsList>
                    <TabsTrigger value="mine" data-testid="tab-my-exit">My resignation</TabsTrigger>
                    {canManage && <TabsTrigger value="all" data-testid="tab-all-exit">All resignations</TabsTrigger>}
                </TabsList>

                <TabsContent value="mine" className="mt-4">
                    {mine?.id ? (
                        <GlassCard className="p-6">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <h3 className="font-heading text-xl font-bold">Resignation submitted</h3>
                                    <p className="text-sm text-muted-foreground">Last working day: {mine.last_working_day}</p>
                                </div>
                                <StatusBadge status={mine.status} />
                            </div>
                            <p className="mt-4 text-sm">{mine.reason}</p>
                            {mine.notes && <p className="mt-2 text-sm text-muted-foreground">{mine.notes}</p>}
                        </GlassCard>
                    ) : (
                        <GlassCard className="p-6 max-w-2xl">
                            <h3 className="font-heading text-xl font-bold mb-1">Submit resignation</h3>
                            <p className="text-sm text-muted-foreground mb-4">We're sorry to see you go. Please share a few details.</p>
                            <ResignForm onSubmitted={load} />
                        </GlassCard>
                    )}
                </TabsContent>

                {canManage && (
                    <TabsContent value="all" className="mt-4">
                        {all.length === 0 ? (
                            <GlassCard><EmptyState icon={DoorOpen} title="No resignations" description="No one has submitted resignation." /></GlassCard>
                        ) : (
                            <div className="space-y-3">
                                {all.map((r) => <AdminCard key={r.id} r={r} onUpdate={load} />)}
                            </div>
                        )}
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

function ResignForm({ onSubmitted }) {
    const [form, setForm] = useState({ reason: "", last_working_day: "", notes: "" });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!window.confirm("Submit resignation? This action notifies HR.")) return;
        setLoading(true);
        try {
            await api.post("/exit/resign", form);
            toast.success("Resignation submitted");
            onSubmitted();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3">
            <div><Label>Reason</Label><Textarea required rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" data-testid="resign-reason" /></div>
            <div><Label>Last working day</Label><Input required type="date" value={form.last_working_day} onChange={(e) => setForm({ ...form, last_working_day: e.target.value })} className="mt-1" data-testid="resign-lwd" /></div>
            <div><Label>Additional notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            <Button type="submit" disabled={loading} className="rounded-full" data-testid="resign-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit resignation"}
            </Button>
        </form>
    );
}

function AdminCard({ r, onUpdate }) {
    const approve = async () => {
        await api.post(`/exit/${r.id}/approve`);
        toast.success("Approved");
        onUpdate();
    };
    const finalize = async () => {
        await api.post(`/exit/${r.id}/finalize`);
        toast.success("Exit finalized");
        onUpdate();
    };
    const toggle = async (item, returned) => {
        await api.post(`/exit/${r.id}/asset`, { item, returned });
        onUpdate();
    };
    return (
        <GlassCard className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-heading font-semibold">{r.user_name}</div>
                    <div className="text-xs text-muted-foreground">Last day: {r.last_working_day}</div>
                    <p className="text-sm mt-2 max-w-xl">{r.reason}</p>
                </div>
                <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    {r.status === "pending" && <Button onClick={approve} size="sm" className="rounded-full">Approve</Button>}
                    {r.status === "approved" && <Button onClick={finalize} size="sm" className="rounded-full">Finalize exit</Button>}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                {(r.asset_checklist || []).map((c) => (
                    <button
                        key={c.item}
                        onClick={() => toggle(c.item, !c.returned)}
                        className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition ${c.returned ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}
                    >
                        {c.returned && <Check className="w-3 h-3" />} {c.item}
                    </button>
                ))}
            </div>
        </GlassCard>
    );
}
